/** What a `Ratio` renders: the counts, and a percentage only where one means something. */
export interface RatioParts {
  /** Both counts, always — "3 of 4". */
  text: string;
  /** Whole percent, or null where there is nothing to divide by. */
  percent: string | null;
  /** For the bar; null exactly when `percent` is. */
  fraction: number | null;
}

const count = (n: number) => n.toLocaleString('en-US');

/**
 * ADR 0012 Decision 16: every percentage shows the counts behind it, by construction — which is why
 * this takes a numerator and a denominator and never a ratio. A ratio alone cannot be turned back
 * into the counts, so a component accepting one could not keep that promise.
 *
 * A denominator of zero has no percentage: "0 of 0" is true, "0%" of nothing is invented.
 */
export function ratioParts(numerator: number, denominator: number): RatioParts {
  const text = `${count(numerator)} of ${count(denominator)}`;
  if (denominator === 0) return { text, percent: null, fraction: null };

  const fraction = numerator / denominator;
  // A share above nothing never reads as nothing: "2 of 1,403 · 0%" says nobody where somebody is. Decided
  // on the UNROUNDED share — rounding first is exactly what turned 0.14% into 0% — so anything above zero
  // and under one percent reads "<1%", and only a true zero reads "0%". Here, so every card gets it.
  if (fraction > 0 && fraction < 0.01) return { text, percent: '<1%', fraction };
  // Math.round rounds halves up, which for these non-negative counts is away from zero.
  return { text, percent: `${Math.round(fraction * 100)}%`, fraction };
}
